import { useMemo } from "react";

import { mean } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext } from "@dnd-kit/sortable";
import { useMetricsExplorerContext } from "@features/metrics-explorer/hooks";
import { useFeatureFlags } from "@hooks/http";

import { MetricCard } from "../metric-card/metric-card";

import {
  buildAnalysisRows,
  type MetricsExplorerCorrelationRow,
} from "./correlation-utils";

interface Props {
  metricCardWidth: number;
}

export const MetricCardList = ({ metricCardWidth }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { hasFeatureFlag } = useFeatureFlags();

  const {
    metricsData,
    benchmarksData,
    filters,
    tiles,
    buildMetrics,
    setTiles,
  } = useMetricsExplorerContext();

  const correlations = useMemo(() => {
    const rows: Record<string, MetricsExplorerCorrelationRow> = {};

    const activeTile = tiles.find((e) => e.active);

    if (!activeTile) {
      return rows;
    }

    const activeMetricsData = metricsData.find(
      (e) => e.tileId === activeTile.id && e.status === "done",
    );

    if (!activeMetricsData) {
      return rows;
    }

    const analysisRowsByTileId = buildAnalysisRows({
      metricsData,
      tiles,
      attributionModel: filters.attributionModel,
      attributionWindow: filters.attributionWindow,
      accountingMode: filters.accountingMode,
      buildMetrics,
    });

    if (!analysisRowsByTileId[activeTile.id]) {
      return rows;
    }

    const actualValues = analysisRowsByTileId[activeTile.id].values;
    const actualValuesMean = mean(actualValues);

    Object.entries(analysisRowsByTileId).forEach(([id, analysisRow]) => {
      const predictedValues = analysisRow.values;
      let r = 0;

      const predictedValuesMean = mean(predictedValues);

      const numerator = actualValues.reduce(
        (acc, val, idx) =>
          (acc +=
            (val - actualValuesMean) *
            ((predictedValues[idx] || 0) - predictedValuesMean)),
        0,
      );
      const denominator =
        actualValues.reduce(
          (acc, val) => (acc += Math.pow(val - actualValuesMean, 2)),
          0,
        ) *
        actualValues.reduce(
          (acc, _, idx) =>
            (acc += Math.pow(
              (predictedValues[idx] || 0) - predictedValuesMean,
              2,
            )),
          0,
        );

      r = numerator / Math.sqrt(denominator);

      rows[id] = {
        r: Math.round(r * 100) / 100,
        canBeCalculated: actualValues.length === predictedValues.length,
      };
    });

    return rows;
  }, [
    metricsData,
    tiles,
    filters.attributionModel,
    filters.attributionWindow,
    filters.accountingMode,
    buildMetrics,
  ]);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = tiles.findIndex(({ id }) => id === active.id);
      const newIndex = tiles.findIndex(({ id }) => id === over?.id);
      const newOrdering = arrayMove(tiles, oldIndex, newIndex);

      prettyLogEventPage({
        domain: "Metrics Explorer",
        action: "onChange",
        additionalActionInfo: "Reorder tiles",
        data: {
          newTiles: newOrdering,
          pageContext: { tiles },
        },
      });

      setTiles(newOrdering);
    }
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToParentElement]}
        onDragEnd={handleDragEnd}
        autoScroll={false}
      >
        <SortableContext items={tiles.map(({ id }) => id)}>
          {tiles.map((tile, index) => {
            const md = metricsData.find((e) => e.tileId === tile.id);
            const bd = benchmarksData.find((e) => e.tileId === tile.id);

            if (!md) {
              return null;
            }

            return (
              <MetricCard
                key={tile.id}
                width={metricCardWidth}
                index={index}
                metricData={md}
                benchmarkData={bd}
                correlation={correlations[tile.id]}
                onDuplicate={() => {
                  const newTiles = [
                    ...tiles,
                    {
                      ...tile,
                      active: false,
                      id: uuidv4(),
                      index: tiles.length,
                    },
                  ];

                  prettyLogEventPage({
                    domain: "Metrics Explorer",
                    action: "onClick",
                    additionalActionInfo: "Duplicate tile",
                    data: {
                      newTiles,
                      pageContext: { tiles },
                    },
                  });

                  setTiles(newTiles);
                }}
                onDelete={() => {
                  let newTiles = tiles
                    .filter((e) => e.id !== tile.id)
                    .map((e, idx) => ({ ...e, index: idx }));
                  const activeIndex = newTiles.findIndex((e) => e.active);
                  const isCorrelationDisabled = !hasFeatureFlag(
                    "enableMetricsExplorerCorrelationAnalysis",
                  );

                  if (
                    activeIndex === -1 ||
                    (isCorrelationDisabled && activeIndex > 1)
                  ) {
                    newTiles = newTiles.map((e, idx) => ({
                      ...e,
                      active: idx === 0,
                    }));
                  }

                  prettyLogEventPage({
                    domain: "Metrics Explorer",
                    action: "onClick",
                    additionalActionInfo: "Delete tile",
                    data: {
                      newTiles,
                      pageContext: { tiles },
                    },
                  });

                  setTiles(newTiles);
                }}
              />
            );
          })}
        </SortableContext>
      </DndContext>
    </>
  );
};
